import { Box, Link, Stack } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { BlankCell } from '../../../../../common/components/Cells/BlankCell';
import { FilterConfiguration } from '../../../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../../../../common/controllers/PaginatedTableController';
import { getOperationContextLabel } from '../../../../../common/operations/enums/OperationContext';
import { getOperationTypeLabel } from '../../../../../common/operations/enums/OperationType';
import { DataGridStyleOverrides } from '../../../../../common/style/data-grid-styles';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../../../common/types/pagination';
import { UrlHelper } from '../../../../../common/utils/UrlHelper';
import { PATHS } from '../../../../../routing/routes';
import { Operation } from '../models/Operation';
import { DEFAULT_INSTANCE_OPERATIONS_PAGE_SIZE, InstanceRepository } from '../services/InstanceRepository';

type Props = {
  reportId: number;
  issueNumber: number;
  instanceNumber: number;
  instanceId: number;

  initialOperations?: PaginationResult<Operation> | null;

  parentLoading: boolean;
};

type State = PaginatedTableState & {};

export class InstanceOperationsController extends PaginatedTableController<Operation, Props, State> {
  private readonly repository: InstanceRepository;

  static defaultProps = {
    initialOperations: null,
  };

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.repository = new InstanceRepository();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  protected isLoading(): boolean {
    return super.isLoading() || this.props.parentLoading;
  }

  protected getPrefilledConfig() {
    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialOperations,
    } as PrefilledDataConfig<Operation>;
  }

  protected getDefaultPageSize(): number {
    return DEFAULT_INSTANCE_OPERATIONS_PAGE_SIZE;
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('operations') };
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<Operation>> {
    return {
      ...super.getAdditionalDataGridProps(),
      classes: {
        row: DataGridStyleOverrides.ROW_AUTO_HEIGHT,
        virtualScrollerContent: DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT,
        virtualScrollerRenderZone: DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT,
      },
    };
  }

  protected renderTable(tableComponent: React.ReactElement) {
    return <Box className="instance-operations">{tableComponent}</Box>;
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Operation>> {
    try {
      return await this.repository.getInstanceOperations(this.props.instanceId, cursorInfo);
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  protected getFieldConfigurations(): FieldConfiguration<Operation>[] {
    const { reportId } = this.props;
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (item, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_OPERATION, { reportId: reportId.toString(), operationId: item.getId().toString() })}
            aria-label={t('a11y.report.issues.operationIdLabel', { operationId: item.getId() }) as string}
          >
            {item.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'type',
        label: t('type'),
        description: t('type'),
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getOperationTypeLabel(item.getType())}</TypographyOverflow>,
      },
      {
        fieldKey: 'context',
        label: t('context'),
        description: t('context'),
        columnProps: { maxWidth: 125 },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getOperationContextLabel(item.getContext())}</TypographyOverflow>,
      },
      {
        fieldKey: 'algorithms',
        label: t('algorithms'),
        description: t('algorithms'),
        render: (item, { hasFocus }) => {
          const algorithms = item.getAlgorithms();
          if (algorithms.length > 0) {
            return <TypographyOverflow hasFocus={hasFocus}>{algorithms.join(', ')}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('none')} />;
        },
      },
      {
        fieldKey: 'keys',
        label: t('keys'),
        description: t('keys'),
        render: (item, { tabIndex, hasFocus }) => {
          const keys = item.getKeys();
          if (keys.length === 0) return <BlankCell />;
          return (
            <Stack sx={{ maxWidth: '100%', py: 1 }} spacing={1}>
              {keys.map((k) => (
                <Link
                  key={k.getId()}
                  tabIndex={tabIndex}
                  href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, { reportId: reportId.toString(), keyId: k.getId().toString() })}
                  aria-label={t('a11y.report.issues.operationKeyIdLabel', { keyId: k.getId(), operationId: item.getId() }) as string}
                >
                  <TypographyOverflow hasFocus={hasFocus}>{k.getDescription()}</TypographyOverflow>
                </Link>
              ))}
            </Stack>
          );
        },
      },
    ];
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }
}
